import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { Conflict, RevisionOperation, VisitSnapshot } from './types'

interface StoredMediaRecord {
  id: string
  visitId: string
  data?: ArrayBuffer
  mimeType?: string
  blob?: Blob
  uploadId?: string
  objectKey?: string
  completedParts?: Array<{ ETag: string; PartNumber: number; ChecksumSHA256: string }>
  uploaded: boolean
  updatedAt: string
}

export type StoredMedia = Omit<StoredMediaRecord, 'blob' | 'data' | 'mimeType'> & { blob: Blob }

interface AuditDb extends DBSchema {
  visits: {
    key: string
    value: VisitSnapshot
  }
  operations: {
    key: string
    value: RevisionOperation
    indexes: { byVisit: string; byCreatedAt: string }
  }
  media: {
    key: string
    value: StoredMediaRecord
    indexes: { byVisit: string; byUploaded: number }
  }
  conflicts: {
    key: string
    value: Conflict
  }
  settings: {
    key: string
    value: { key: string; value: string }
  }
}

let dbPromise: Promise<IDBPDatabase<AuditDb>> | null = null

export function getDb() {
  if (!dbPromise) {
    dbPromise = openDB<AuditDb>('climatise-site-audit', 2, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          db.createObjectStore('visits', { keyPath: 'id' })
          const operations = db.createObjectStore('operations', { keyPath: 'id' })
          operations.createIndex('byVisit', 'visitId')
          operations.createIndex('byCreatedAt', 'createdAt')
          const media = db.createObjectStore('media', { keyPath: 'id' })
          media.createIndex('byVisit', 'visitId')
          media.createIndex('byUploaded', 'uploaded')
          db.createObjectStore('conflicts', { keyPath: 'operation.id' })
          db.createObjectStore('settings', { keyPath: 'key' })
        }
      },
    })
  }
  return dbPromise
}

export async function storageHealthcheck(): Promise<boolean> {
  try {
    const db = await getDb()
    const key = `health:${crypto.randomUUID()}`
    await db.put('settings', { key, value: new Date().toISOString() })
    await db.delete('settings', key)
    if (navigator.storage?.persist) {
      void navigator.storage.persist().catch(() => false)
    }
    return true
  } catch {
    return false
  }
}

export async function readVisit(id: string): Promise<VisitSnapshot | undefined> {
  return (await getDb()).get('visits', id)
}

export async function findVisitBySlug(slug: string): Promise<VisitSnapshot | undefined> {
  const visits = await (await getDb()).getAll('visits')
  return visits.find((visit) => visit.slug === slug)
}

export function coalesceOperations(
  existing: RevisionOperation[],
  incoming: RevisionOperation[],
): RevisionOperation[] {
  const result = [...existing].sort((a, b) => a.createdAt.localeCompare(b.createdAt))
  for (const operation of incoming) {
    const matching = result.filter((item) => (
      item.recordType === operation.recordType && item.recordId === operation.recordId
    ))
    const previous = matching.at(-1)
    if (previous && previous.attempts === 0) {
      const expectedRevision = previous.expectedRevision
      const createdLocallyThenDeleted = operation.action === 'delete'
        && expectedRevision === 0
        && previous.action === 'upsert'
      if (createdLocallyThenDeleted && operation.recordType === 'observation') {
        result.push({ ...operation, expectedRevision: expectedRevision + 1 })
      } else {
        result.splice(result.findIndex((item) => item.id === previous.id), 1)
        if (!createdLocallyThenDeleted) result.push({ ...operation, expectedRevision })
      }
    } else {
      result.push({
        ...operation,
        expectedRevision: previous ? previous.expectedRevision + 1 : operation.expectedRevision,
      })
    }
  }
  return result.sort((a, b) => a.createdAt.localeCompare(b.createdAt))
}

export async function saveVisitLocally(
  visit: VisitSnapshot,
  operations: RevisionOperation[] = [],
): Promise<void> {
  const db = await getDb()
  const tx = db.transaction(['visits', 'operations'], 'readwrite', { durability: 'strict' })
  await tx.objectStore('visits').put(visit)
  const queued = await tx.objectStore('operations').index('byVisit').getAll(visit.id)
  const merged = coalesceOperations(queued, operations)
  await Promise.all(queued.map((item) => tx.objectStore('operations').delete(item.id)))
  await Promise.all(merged.map((item) => tx.objectStore('operations').put(item)))
  await tx.done
}

export async function getPendingOperations(visitId: string): Promise<RevisionOperation[]> {
  const operations = await (await getDb()).getAllFromIndex('operations', 'byVisit', visitId)
  return operations.sort((a, b) => a.createdAt.localeCompare(b.createdAt))
}

export async function claimPendingOperations(visitId: string, limit = 100): Promise<RevisionOperation[]> {
  const db = await getDb()
  const tx = db.transaction('operations', 'readwrite', { durability: 'strict' })
  const queued = await tx.store.index('byVisit').getAll(visitId)
  const claimed = queued
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .slice(0, limit)
    .map((operation) => ({ ...operation, attempts: operation.attempts + 1 }))
  await Promise.all(claimed.map((operation) => tx.store.put(operation)))
  await tx.done
  return claimed
}

export async function acknowledgeOperations(ids: string[]): Promise<void> {
  const db = await getDb()
  const tx = db.transaction('operations', 'readwrite')
  await Promise.all(ids.map((id) => tx.store.delete(id)))
  await tx.done
}

export async function rebasePendingRecord(
  visitId: string,
  recordType: RevisionOperation['recordType'],
  recordId: string,
  expectedRevision: number,
): Promise<void> {
  const db = await getDb()
  const operations = await db.getAllFromIndex('operations', 'byVisit', visitId)
  const tx = db.transaction('operations', 'readwrite')
  for (const operation of operations) {
    if (operation.recordType === recordType && operation.recordId === recordId) {
      await tx.store.put({ ...operation, expectedRevision })
    }
  }
  await tx.done
}

export async function moveOperationToConflict(conflict: Conflict): Promise<void> {
  const operation = conflict.operation
  const db = await getDb()
  const tx = db.transaction(['operations', 'conflicts'], 'readwrite', { durability: 'strict' })
  const operationStore = tx.objectStore('operations')
  const operations = await operationStore.index('byVisit').getAll(operation.visitId)
  const later = operations
    .filter((item) => (
      item.id !== operation.id
      && item.recordType === operation.recordType
      && item.recordId === operation.recordId
    ))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
  await operationStore.delete(operation.id)
  await Promise.all(later.map((item, index) => operationStore.put({
    ...item,
    expectedRevision: conflict.serverRevision + index,
  })))
  await tx.objectStore('conflicts').put(conflict)
  await tx.done
}

export async function resolveStoredConflict(
  visit: VisitSnapshot,
  conflict: Conflict,
  replacementOperations: RevisionOperation[],
  discardRecordOperations: boolean,
): Promise<void> {
  const db = await getDb()
  const tx = db.transaction(['visits', 'operations', 'conflicts'], 'readwrite', { durability: 'strict' })
  const operationStore = tx.objectStore('operations')
  const queued = await operationStore.index('byVisit').getAll(visit.id)
  const retained = discardRecordOperations
    ? queued.filter((item) => (
        item.recordType !== conflict.operation.recordType
        || item.recordId !== conflict.operation.recordId
      ))
    : queued
  const merged = coalesceOperations(retained, replacementOperations)
  await tx.objectStore('visits').put(visit)
  await Promise.all(queued.map((item) => operationStore.delete(item.id)))
  await Promise.all(merged.map((item) => operationStore.put(item)))
  await tx.objectStore('conflicts').delete(conflict.operation.id)
  await tx.done
}

export async function getConflicts(): Promise<Conflict[]> {
  return (await getDb()).getAll('conflicts')
}

export async function storeMedia(id: string, visitId: string, blob: Blob): Promise<void> {
  const data = await blob.arrayBuffer()
  await (await getDb()).put('media', {
    id,
    visitId,
    data,
    mimeType: blob.type,
    uploaded: false,
    updatedAt: new Date().toISOString(),
  })
}

function hydrateMedia(item: StoredMediaRecord): StoredMedia {
  return {
    id: item.id,
    visitId: item.visitId,
    uploadId: item.uploadId,
    objectKey: item.objectKey,
    completedParts: item.completedParts,
    uploaded: item.uploaded,
    updatedAt: item.updatedAt,
    blob: item.blob ?? new Blob([item.data ?? new ArrayBuffer(0)], { type: item.mimeType }),
  }
}

export async function getMedia(id: string): Promise<StoredMedia | undefined> {
  const item = await (await getDb()).get('media', id)
  return item ? hydrateMedia(item) : undefined
}

export async function getPendingMedia(visitId: string): Promise<StoredMedia[]> {
  const all = await (await getDb()).getAllFromIndex('media', 'byVisit', visitId)
  return all.filter((item) => !item.uploaded).map(hydrateMedia)
}

export async function updateMediaUpload(
  id: string,
  patch: Partial<Pick<StoredMediaRecord, 'uploadId' | 'objectKey' | 'completedParts' | 'uploaded'>>,
): Promise<void> {
  const db = await getDb()
  const current = await db.get('media', id)
  if (!current) throw new Error('Media item not found')
  await db.put('media', { ...current, ...patch, updatedAt: new Date().toISOString() })
}

export async function resetMediaUpload(id: string): Promise<void> {
  const db = await getDb()
  const current = await db.get('media', id)
  if (!current) throw new Error('Media item not found')
  const reset = { ...current }
  delete reset.uploadId
  delete reset.objectKey
  delete reset.completedParts
  await db.put('media', { ...reset, uploaded: false, updatedAt: new Date().toISOString() })
}

export async function mediaUrl(id: string): Promise<string | null> {
  const media = await getMedia(id)
  return media ? URL.createObjectURL(media.blob) : null
}

export async function clearVisit(id: string): Promise<void> {
  const db = await getDb()
  const operations = await db.getAllFromIndex('operations', 'byVisit', id)
  const media = await db.getAllFromIndex('media', 'byVisit', id)
  const tx = db.transaction(['visits', 'operations', 'media'], 'readwrite')
  await tx.objectStore('visits').delete(id)
  await Promise.all(operations.map((item) => tx.objectStore('operations').delete(item.id)))
  await Promise.all(media.map((item) => tx.objectStore('media').delete(item.id)))
  await tx.done
}
